import React, { useState, useRef, useEffect } from 'react';
import { 
  TrendingUp, 
  Shield, 
  Wallet,
  BarChart3,
  Play,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Sparkles,
  Star,
  Zap,
  Pencil,
  Palette,
  Trash2,
  Image as ImageIcon,
  Settings,
  X,
  Plus,
  GripVertical
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';

// Hero images
const HERO_IMAGES = {
  dashboard: 'https://images.unsplash.com/photo-1660970781103-ba6749cb9ce3?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  growth: 'https://images.unsplash.com/photo-1579227114496-27346f474519?crop=entropy&cs=srgb&fm=jpg&w=800&q=85'
};

// Icon mapping
const iconMap = {
  TrendingUp,
  Shield,
  Wallet,
  BarChart3,
  CheckCircle,
  Zap,
  Sparkles
};

// Color presets
const COLOR_PRESETS = [
  '#3a7189', '#93a5af', '#1c262d', '#0d1218',
  '#3B82F6', '#EF4444', '#EAB308', '#FFFFFF',
  '#10B981', '#8B5CF6', '#EC4899', '#F97316'
];

// Default content - MUST match LandingPage.js exactly
const DEFAULT_CONTENT = {
  header: {
    menuItems: [
      { label: "Como Funciona", href: "#como-funciona" },
      { label: "Benefícios", href: "#beneficios" },
      { label: "Testemunhos", href: "#testimonios" },
      { label: "Contacto", href: "#contacto" }
    ],
    loginText: "Iniciar Sessão",
    registerText: "Criar Conta"
  },
  hero: {
    badge: "Smart Global Financial Insights",
    title: "O Seu Dinheiro,",
    titleHighlight: "Sem Limites",
    subtitle: "Junte-se a milhares de investidores que confiam em nós. Rendimentos atrativos, gestão profissional e total transparência.",
    ctaPrimary: "Começar Agora",
    ctaSecondary: "Saber Mais",
    stats: [
      { value: "4%", label: "Rendimento mensal" },
      { value: "100+", label: "Clientes ativos" },
      { value: "24/7", label: "Acesso total" }
    ]
  },
  howItWorks: {
    title: "Investir Nunca Foi Tão Fácil",
    subtitle: "Comece em minutos, não em dias",
    steps: [
      { step: "01", title: "Crie a Sua Conta", description: "Registo rápido e 100% digital. Sem burocracia." },
      { step: "02", title: "Deposite Fundos", description: "Transferência bancária segura e verificada." },
      { step: "03", title: "Veja o Seu Dinheiro Crescer", description: "Rendimentos mensais diretamente na sua conta." }
    ]
  },
  benefits: {
    title: "Tudo o Que Precisa",
    subtitle: "Uma plataforma completa para os seus investimentos",
    items: [
      { icon: "TrendingUp", title: "Rendimentos Atrativos", description: "Taxas competitivas do mercado com pagamentos mensais garantidos." },
      { icon: "Shield", title: "Máxima Segurança", description: "O seu capital protegido com os mais altos padrões de segurança." },
      { icon: "Wallet", title: "Gestão Simples", description: "Plataforma intuitiva para controlar os seus investimentos." },
      { icon: "BarChart3", title: "Transparência Total", description: "Acesso em tempo real a todos os seus movimentos." }
    ],
    features: ["100% digital", "Sem comissões ocultas", "Levantamentos flexíveis", "Relatórios detalhados", "Suporte dedicado", "App móvel"]
  },
  testimonials: {
    title: "O Que Dizem os Nossos Clientes",
    subtitle: "Milhares de investidores confiam em nós",
    items: [
      { name: "Maria Silva", role: "Empresária", rating: 5, text: "Excelente plataforma. Os rendimentos são consistentes e o serviço ao cliente é excecional." },
      { name: "Carlos Santos", role: "Engenheiro", rating: 5, text: "Há anos que procurava uma opção segura para investir. A SGFI Capital superou as minhas expectativas." },
      { name: "Ana Ferreira", role: "Médica", rating: 5, text: "A transparência e facilidade de uso convenceram-me desde o primeiro dia. Muito recomendável." }
    ]
  },
  videos: {
    title: "Conheça a Nossa Plataforma",
    subtitle: "Descubra como funciona",
    items: [
      { id: 1, title: "Como Funciona", description: "Guia completo de investimento.", embedUrl: "", placeholder: true },
      { id: 2, title: "Abertura de Conta", description: "Processo passo a passo.", embedUrl: "", placeholder: true },
      { id: 3, title: "Segurança", description: "Conheça as nossas medidas.", embedUrl: "", placeholder: true }
    ]
  },
  cta: {
    title: "Pronto Para Começar?",
    subtitle: "Abra a sua conta hoje e comece a ver o seu dinheiro crescer.",
    buttonText: "Criar Conta Grátis"
  },
  contact: {
    title: "Tem Dúvidas?",
    subtitle: "A nossa equipa está disponível para o ajudar.",
    email: "capitalsgfi@gmail.com",
    location: "Lisboa, Portugal",
    legalTitle: "Informação Legal",
    legalText: "SGFI Capital - Smart Global Financial Insights. Todos os investimentos envolvem risco."
  },
  faq: {
    title: "Perguntas Frequentes",
    subtitle: "Encontre respostas às questões mais comuns sobre a nossa plataforma.",
    items: [
      {
        id: "faq1",
        question: "Como posso abrir uma conta na SGFI Capital?",
        answer: "Para abrir uma conta, basta clicar em 'Criar Conta', preencher o formulário de registo com os seus dados pessoais e carregar um documento de identificação. A sua conta será analisada e aprovada em até 48 horas úteis."
      },
      {
        id: "faq2",
        question: "Qual é o montante mínimo para investir?",
        answer: "O montante mínimo para começar a investir na SGFI Capital é de €1.000. Este valor permite-lhe aceder a todas as funcionalidades da plataforma e começar a gerar rendimentos mensais."
      },
      {
        id: "faq3",
        question: "Como são calculados os rendimentos?",
        answer: "Os rendimentos são calculados mensalmente com base no saldo da sua conta e na taxa de rendimento acordada. Os depósitos feitos durante o mês são calculados proporcionalmente aos dias restantes do mês."
      },
      {
        id: "faq4",
        question: "Como posso levantar os meus fundos?",
        answer: "Pode solicitar um levantamento a qualquer momento através da sua área de cliente. Os levantamentos são processados em até 5 dias úteis e transferidos para a conta bancária registada no seu perfil."
      },
      {
        id: "faq5",
        question: "Os meus dados estão seguros?",
        answer: "Sim, utilizamos encriptação de última geração para proteger todos os seus dados. Cumprimos integralmente o RGPD e as normas de segurança bancária europeias."
      }
    ]
  }
};

// Helper to get content with defaults
const getContent = (content, path, defaultValue) => {
  const keys = path.split('.');
  let value = content;
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) break;
  }
  if (value !== undefined) return value;
  
  // Get from DEFAULT_CONTENT
  let defValue = DEFAULT_CONTENT;
  for (const key of keys) {
    defValue = defValue?.[key];
    if (defValue === undefined) break;
  }
  return defValue !== undefined ? defValue : defaultValue;
};

// Editable Text Component
const EditableText = ({ value, onChange, path, className, tag: Tag = 'span', multiline = false, editMode }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    onChange(path, localValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    }
    if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  if (editMode === 'preview') {
    return <Tag className={className}>{value}</Tag>;
  }

  if (isEditing) {
    return multiline ? (
      <Textarea
        ref={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`bg-white/10 border border-[#3a7189] text-inherit rounded px-2 py-1 w-full min-h-[60px] focus:ring-2 focus:ring-[#3a7189] ${className}`}
        style={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}
      />
    ) : (
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`bg-white/10 border border-[#3a7189] text-inherit rounded px-2 py-1 w-full focus:ring-2 focus:ring-[#3a7189] focus:outline-none ${className}`}
        style={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}
      />
    );
  }

  return (
    <Tag 
      className={`${className} cursor-pointer hover:outline hover:outline-2 hover:outline-[#3a7189] hover:outline-dashed rounded transition-all group relative`}
      onClick={() => setIsEditing(true)}
    >
      {value || 'Clique para editar'}
      <span className="absolute -top-6 left-0 bg-[#3a7189] text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap flex items-center gap-1">
        <Pencil className="w-3 h-3" /> Editar
      </span>
    </Tag>
  );
};

// Editable Section Wrapper with hover controls
const EditableSection = ({ id, children, onDelete, onColorChange, editMode, bgColor, className = '' }) => {
  const [showControls, setShowControls] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  if (editMode === 'preview') {
    return <section className={className} style={{ backgroundColor: bgColor }}>{children}</section>;
  }

  return (
    <section 
      className={`relative ${className}`}
      style={{ backgroundColor: bgColor }}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => {
        setShowControls(false);
        setShowColorPicker(false);
      }}
      data-section-id={id}
    >
      {/* Section Controls */}
      {showControls && (
        <div className="absolute top-2 right-2 z-50 flex items-center gap-1 bg-[#1c262d] rounded-lg p-1 shadow-lg border border-white/20">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="p-1.5 text-[#93a5af] hover:text-white hover:bg-white/10 rounded transition-colors"
            title="Alterar cor de fundo"
          >
            <Palette className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete && onDelete(id)}
            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
            title="Ocultar secção"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="w-px h-4 bg-white/20" />
          <span className="px-2 text-xs text-[#93a5af] capitalize">{id}</span>
        </div>
      )}

      {/* Color Picker Dropdown */}
      {showColorPicker && (
        <div className="absolute top-12 right-2 z-50 bg-[#1c262d] rounded-lg p-3 shadow-xl border border-white/20">
          <p className="text-xs text-[#93a5af] mb-2">Cor de fundo</p>
          <div className="grid grid-cols-4 gap-2">
            {COLOR_PRESETS.map((color) => (
              <button
                key={color}
                onClick={() => {
                  onColorChange && onColorChange(id, color);
                  setShowColorPicker(false);
                }}
                className="w-7 h-7 rounded border-2 border-white/20 hover:border-white/50 transition-all hover:scale-110"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {children}

      {/* Section Border on Hover */}
      {showControls && (
        <div className="absolute inset-0 border-2 border-dashed border-[#3a7189]/50 pointer-events-none rounded" />
      )}
    </section>
  );
};

// Editable Card Component for Benefits, Testimonials, etc.
const EditableCard = ({ children, index, onDelete, editMode, className = '' }) => {
  const [showControls, setShowControls] = useState(false);

  if (editMode === 'preview') {
    return <div className={className}>{children}</div>;
  }

  return (
    <div 
      className={`relative ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {showControls && (
        <div className="absolute -top-2 -right-2 z-40 flex items-center gap-1">
          <button
            onClick={() => onDelete && onDelete(index)}
            className="p-1 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors"
            title="Eliminar elemento"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {children}
      {showControls && (
        <div className="absolute inset-0 border-2 border-dashed border-[#3a7189]/50 pointer-events-none rounded-2xl" />
      )}
    </div>
  );
};

// Star Rating Editor
const EditableRating = ({ value = 5, onChange, editMode }) => {
  const [hoverValue, setHoverValue] = useState(0);

  if (editMode === 'preview') {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`w-4 h-4 ${i < value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <button
          key={i}
          onMouseEnter={() => setHoverValue(i + 1)}
          onMouseLeave={() => setHoverValue(0)}
          onClick={() => onChange(i + 1)}
          className="focus:outline-none transition-transform hover:scale-125"
        >
          <Star 
            className={`w-4 h-4 transition-colors ${
              i < (hoverValue || value) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function VisualEditorCanvas({ content, editMode, onUpdateContent, sectionVisibility, previewDevice }) {
  const getIcon = (iconName) => {
    return iconMap[iconName] || TrendingUp;
  };

  const handleDeleteSection = (sectionId) => {
    // This would hide the section
    // Log silenced: 'Delete section:', sectionId);
  };

  const handleSectionColorChange = (sectionId, color) => {
    // Update section background color
    onUpdateContent(`styles.${sectionId}Background`, color);
  };

  const handleDeleteItem = (section, arrayPath, index) => {
    const currentArray = content?.[section]?.[arrayPath] || [];
    const newArray = currentArray.filter((_, i) => i !== index);
    onUpdateContent(`${section}.${arrayPath}`, newArray);
  };

  const handleAddItem = (section, arrayPath, template) => {
    const currentArray = content?.[section]?.[arrayPath] || [];
    onUpdateContent(`${section}.${arrayPath}`, [...currentArray, template]);
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full bg-[#0d1218] text-[#93a5af]">
        <p>A carregar...</p>
      </div>
    );
  }

  // Merge content with defaults - deep merge for arrays
  const mergedContent = {
    ...DEFAULT_CONTENT,
    ...content,
    header: { 
      ...DEFAULT_CONTENT.header, 
      ...content?.header,
      menuItems: content?.header?.menuItems?.length > 0 ? content.header.menuItems : DEFAULT_CONTENT.header.menuItems
    },
    hero: { 
      ...DEFAULT_CONTENT.hero, 
      ...content?.hero,
      stats: content?.hero?.stats?.length > 0 ? content.hero.stats : DEFAULT_CONTENT.hero.stats
    },
    howItWorks: { 
      ...DEFAULT_CONTENT.howItWorks, 
      ...content?.howItWorks,
      steps: content?.howItWorks?.steps?.length > 0 ? content.howItWorks.steps : DEFAULT_CONTENT.howItWorks.steps
    },
    benefits: { 
      ...DEFAULT_CONTENT.benefits, 
      ...content?.benefits,
      items: content?.benefits?.items?.length > 0 ? content.benefits.items : DEFAULT_CONTENT.benefits.items,
      features: content?.benefits?.features?.length > 0 ? content.benefits.features : DEFAULT_CONTENT.benefits.features
    },
    testimonials: { 
      ...DEFAULT_CONTENT.testimonials, 
      ...content?.testimonials,
      items: content?.testimonials?.items?.length > 0 ? content.testimonials.items : DEFAULT_CONTENT.testimonials.items
    },
    videos: { 
      ...DEFAULT_CONTENT.videos, 
      ...content?.videos,
      items: content?.videos?.items?.length > 0 ? content.videos.items : DEFAULT_CONTENT.videos.items
    },
    faq: { 
      ...DEFAULT_CONTENT.faq, 
      ...content?.faq,
      items: content?.faq?.items?.length > 0 ? content.faq.items : DEFAULT_CONTENT.faq.items
    },
    cta: { ...DEFAULT_CONTENT.cta, ...content?.cta },
    contact: { ...DEFAULT_CONTENT.contact, ...content?.contact }
  };

  // Dynamic colors from content styles
  const styles = content?.styles || {};
  const accentColor = styles.accentColor || '#3a7189';
  const primaryColor = styles.primaryColor || '#93a5af';
  const heroBackground = styles.heroBackground || '#0d1218';
  const sectionBackground = styles.sectionBackground || '#1c262d';

  return (
    <div style={{ backgroundColor: heroBackground }} className="min-h-full">
      {/* Navigation - Editable */}
      <nav style={{ backgroundColor: `${heroBackground}f0` }} className="backdrop-blur-xl border-b border-white/10 relative group/nav">
        {editMode !== 'preview' && (
          <div className="absolute -top-6 left-4 bg-[#3a7189] text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover/nav:opacity-100 transition-opacity whitespace-nowrap flex items-center gap-1 z-10">
            <Pencil className="w-3 h-3" /> Editar Menu
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-8 w-auto" />
            <div className="flex items-center gap-6 text-sm">
              {(mergedContent.header.menuItems).map((item, index) => (
                <div key={index} className="relative group/item">
                  {editMode !== 'preview' ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => {
                          const newItems = [...(mergedContent.header.menuItems || [])];
                          newItems[index] = { ...newItems[index], label: e.target.value };
                          onUpdateContent('header.menuItems', newItems);
                        }}
                        className="bg-transparent border-b border-dashed border-white/30 hover:border-[#3a7189] focus:border-[#3a7189] focus:outline-none text-center font-medium px-1 w-24"
                        style={{ color: primaryColor }}
                        placeholder="Item"
                      />
                      <button
                        onClick={() => {
                          const newItems = (mergedContent.header.menuItems || []).filter((_, i) => i !== index);
                          onUpdateContent('header.menuItems', newItems);
                        }}
                        className="opacity-0 group-hover/item:opacity-100 text-red-400 hover:text-red-300 p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: primaryColor }} className="font-medium">{item.label}</span>
                  )}
                </div>
              ))}
              {editMode !== 'preview' && (
                <button
                  onClick={() => {
                    const newItems = [...(mergedContent.header.menuItems || []), { label: "Novo", href: "#novo" }];
                    onUpdateContent('header.menuItems', newItems);
                  }}
                  className="text-[#3a7189] hover:text-[#3a9d45] border border-dashed border-[#3a7189]/50 hover:border-[#3a7189] rounded px-2 py-0.5 text-xs flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Item
                </button>
              )}
              <div className="border-l border-white/20 h-6 mx-2" />
              {editMode !== 'preview' ? (
                <input
                  type="text"
                  value={mergedContent.header.loginText || 'Iniciar Sessão'}
                  onChange={(e) => onUpdateContent('header.loginText', e.target.value)}
                  className="bg-transparent border-b border-dashed border-white/30 hover:border-[#3a7189] focus:border-[#3a7189] focus:outline-none text-white font-medium px-1 w-28 text-center"
                  placeholder="Login"
                />
              ) : (
                <button className="text-white font-medium">{mergedContent.header.loginText || 'Iniciar Sessão'}</button>
              )}
              {editMode !== 'preview' ? (
                <input
                  type="text"
                  value={mergedContent.header.registerText || 'Criar Conta'}
                  onChange={(e) => onUpdateContent('header.registerText', e.target.value)}
                  style={{ backgroundColor: accentColor }}
                  className="text-white rounded-full px-4 py-2 text-sm font-semibold border-2 border-dashed border-white/30 hover:border-white focus:border-white focus:outline-none w-28 text-center"
                  placeholder="Registo"
                />
              ) : (
                <button style={{ backgroundColor: accentColor }} className="text-white rounded-full px-4 py-2 text-sm font-semibold">
                  {mergedContent.header.registerText || 'Criar Conta'}
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      {sectionVisibility.hero && (
        <EditableSection 
          id="hero" 
          editMode={editMode}
          bgColor={heroBackground}
          onDelete={handleDeleteSection}
          onColorChange={handleSectionColorChange}
          className="relative py-20 overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute top-1/4 -left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: `${accentColor}25` }} />
          <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: `${primaryColor}15` }} />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left content */}
              <div>
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ backgroundColor: `${accentColor}20`, borderColor: `${accentColor}50`, borderWidth: 1 }}>
                  <Sparkles className="w-3 h-3" style={{ color: accentColor }} />
                  <EditableText
                    value={mergedContent.hero.badge}
                    onChange={onUpdateContent}
                    path="hero.badge"
                    className="text-xs font-medium"
                    style={{ color: primaryColor }}
                    editMode={editMode}
                  />
                </div>
                
                {/* Title */}
                <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight mb-4">
                  <EditableText
                    value={mergedContent.hero.title}
                    onChange={onUpdateContent}
                    path="hero.title"
                    className="block"
                    editMode={editMode}
                  />
                  <span className="block text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${accentColor}, ${primaryColor})` }}>
                    <EditableText
                      value={mergedContent.hero.titleHighlight}
                      onChange={onUpdateContent}
                      path="hero.titleHighlight"
                      editMode={editMode}
                    />
                  </span>
                </h1>
                
                <EditableText
                  tag="p"
                  value={mergedContent.hero.subtitle}
                  onChange={onUpdateContent}
                  path="hero.subtitle"
                  className="text-lg mb-8 max-w-lg leading-relaxed"
                  style={{ color: primaryColor }}
                  multiline
                  editMode={editMode}
                />
                
                {/* CTA Buttons */}
                <div className="flex gap-3">
                  <button className="text-white rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2 transition-colors" style={{ backgroundColor: accentColor, boxShadow: `0 10px 15px -3px ${accentColor}40` }}>
                    <EditableText
                      value={mergedContent.hero.ctaPrimary}
                      onChange={onUpdateContent}
                      path="hero.ctaPrimary"
                      editMode={editMode}
                    />
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button className="border-2 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors" style={{ borderColor: `${primaryColor}50` }}>
                    <EditableText
                      value={mergedContent.hero.ctaSecondary}
                      onChange={onUpdateContent}
                      path="hero.ctaSecondary"
                      editMode={editMode}
                    />
                  </button>
                </div>

              </div>

              {/* Right - Hero Image */}
              <div className="relative">
                <div className="relative mx-auto max-w-sm">
                  <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-[#3a7189]/20 bg-gradient-to-b from-[#1c262d] to-[#0d1218] p-2">
                    <img 
                      src={HERO_IMAGES.dashboard}
                      alt="App Dashboard"
                      className="rounded-[1.5rem] w-full"
                    />
                  </div>
                  
                  {/* Floating cards */}
                  <div className="absolute -left-8 top-1/4 bg-[#1c262d] border border-[#3a7189]/30 rounded-xl shadow-lg shadow-[#3a7189]/10 p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#3a7189]/20 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-[#3a7189]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#93a5af]/70">Rendimiento</p>
                        <p className="text-sm font-bold text-white">+4.0%</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -right-6 bottom-1/3 bg-[#1c262d] border border-[#93a5af]/20 rounded-xl shadow-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#93a5af]/20 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-[#93a5af]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#93a5af]/70">Seguro</p>
                        <p className="text-sm font-bold text-white">100%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats bar */}
            <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl mx-auto">
              {(mergedContent.hero.stats || []).map((stat, index) => (
                <div key={index} className="text-center">
                  <EditableText
                    tag="p"
                    value={stat.value}
                    onChange={onUpdateContent}
                    path={`hero.stats.${index}.value`}
                    className="text-3xl font-bold text-white"
                    editMode={editMode}
                  />
                  <EditableText
                    tag="p"
                    value={stat.label}
                    onChange={onUpdateContent}
                    path={`hero.stats.${index}.label`}
                    className="text-[#93a5af]/70 text-sm mt-1"
                    editMode={editMode}
                  />
                </div>
              ))}
            </div>
          </div>
        </EditableSection>
      )}

      {/* How It Works */}
      {sectionVisibility.howItWorks && (
        <EditableSection 
          id="howItWorks" 
          editMode={editMode}
          bgColor={sectionBackground}
          onDelete={handleDeleteSection}
          onColorChange={handleSectionColorChange}
          className="py-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <EditableText
                tag="h2"
                value={mergedContent.howItWorks.title}
                onChange={onUpdateContent}
                path="howItWorks.title"
                className="text-3xl font-bold text-white mb-3 tracking-tight"
                editMode={editMode}
              />
              <EditableText
                tag="p"
                value={mergedContent.howItWorks.subtitle}
                onChange={onUpdateContent}
                path="howItWorks.subtitle"
                className="text-lg max-w-2xl mx-auto"
                style={{ color: primaryColor }}
                editMode={editMode}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {(mergedContent.howItWorks.steps || []).map((item, index) => (
                <EditableCard 
                  key={index} 
                  index={index}
                  editMode={editMode}
                  onDelete={() => handleDeleteItem('howItWorks', 'steps', index)}
                  className="rounded-2xl p-6 border border-white/5 transition-all"
                  style={{ backgroundColor: heroBackground }}
                >
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl text-white text-sm font-bold mb-4" style={{ backgroundColor: accentColor, boxShadow: `0 10px 15px -3px ${accentColor}40` }}>
                    {item.step}
                  </span>
                  <EditableText
                    tag="h3"
                    value={item.title}
                    onChange={onUpdateContent}
                    path={`howItWorks.steps.${index}.title`}
                    className="text-xl font-bold text-white mb-2"
                    editMode={editMode}
                  />
                  <EditableText
                    tag="p"
                    value={item.description}
                    onChange={onUpdateContent}
                    path={`howItWorks.steps.${index}.description`}
                    className="text-[#93a5af] text-sm"
                    multiline
                    editMode={editMode}
                  />
                </EditableCard>
              ))}
            </div>

            {/* Add Step Button */}
            {editMode === 'visual' && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => handleAddItem('howItWorks', 'steps', {
                    step: String((mergedContent.howItWorks.steps?.length || 0) + 1).padStart(2, '0'),
                    title: 'Novo passo',
                    description: 'Descrição do passo'
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-[#3a7189]/20 text-[#3a7189] rounded-lg hover:bg-[#3a7189]/30 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar passo
                </button>
              </div>
            )}
          </div>
        </EditableSection>
      )}

      {/* Benefits */}
      {sectionVisibility.benefits && (
        <EditableSection 
          id="benefits" 
          editMode={editMode}
          bgColor="#0d1218"
          onDelete={handleDeleteSection}
          onColorChange={handleSectionColorChange}
          className="py-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <EditableText
                tag="h2"
                value={mergedContent.benefits.title}
                onChange={onUpdateContent}
                path="benefits.title"
                className="text-3xl font-bold text-white mb-3 tracking-tight"
                editMode={editMode}
              />
              <EditableText
                tag="p"
                value={mergedContent.benefits.subtitle}
                onChange={onUpdateContent}
                path="benefits.subtitle"
                className="text-lg text-[#93a5af] max-w-2xl mx-auto"
                editMode={editMode}
              />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(mergedContent.benefits.items || []).map((benefit, index) => {
                const Icon = getIcon(benefit.icon);
                return (
                  <EditableCard 
                    key={index}
                    index={index}
                    editMode={editMode}
                    onDelete={() => handleDeleteItem('benefits', 'items', index)}
                    className="bg-[#1c262d] rounded-2xl p-6 border border-white/5 hover:border-[#3a7189]/20 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#3a7189]/10 group-hover:bg-[#3a7189] flex items-center justify-center mb-4 transition-all">
                      <Icon className="w-5 h-5 text-[#3a7189] group-hover:text-white transition-colors" />
                    </div>
                    <EditableText
                      tag="h3"
                      value={benefit.title}
                      onChange={onUpdateContent}
                      path={`benefits.items.${index}.title`}
                      className="text-base font-bold text-white mb-2"
                      editMode={editMode}
                    />
                    <EditableText
                      tag="p"
                      value={benefit.description}
                      onChange={onUpdateContent}
                      path={`benefits.items.${index}.description`}
                      className="text-[#93a5af] text-sm"
                      multiline
                      editMode={editMode}
                    />
                  </EditableCard>
                );
              })}
            </div>

            {/* Add Benefit Button */}
            {editMode === 'visual' && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => handleAddItem('benefits', 'items', {
                    icon: 'CheckCircle',
                    title: 'Novo benefício',
                    description: 'Descrição do benefício'
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-[#3a7189]/20 text-[#3a7189] rounded-lg hover:bg-[#3a7189]/30 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar benefício
                </button>
              </div>
            )}

            {/* Features Checklist */}
            <div className="mt-12 bg-gradient-to-r from-[#3a7189] to-[#247029] rounded-2xl p-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(mergedContent.benefits.features || []).map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
                    <EditableText
                      value={feature}
                      onChange={(_, value) => {
                        const newFeatures = [...(mergedContent.benefits.features || [])];
                        newFeatures[index] = value;
                        onUpdateContent('benefits.features', newFeatures);
                      }}
                      path={`benefits.features.${index}`}
                      className="text-white text-sm"
                      editMode={editMode}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </EditableSection>
      )}

      {/* Testimonials Section */}
      {sectionVisibility.testimonials && (
        <EditableSection 
          id="testimonials" 
          editMode={editMode}
          bgColor="#1c262d"
          onDelete={handleDeleteSection}
          onColorChange={handleSectionColorChange}
          className="py-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <EditableText
                tag="h2"
                value={mergedContent.testimonials.title}
                onChange={onUpdateContent}
                path="testimonials.title"
                className="text-3xl font-bold text-white mb-3 tracking-tight"
                editMode={editMode}
              />
              <EditableText
                tag="p"
                value={mergedContent.testimonials.subtitle}
                onChange={onUpdateContent}
                path="testimonials.subtitle"
                className="text-lg text-[#93a5af] max-w-2xl mx-auto"
                editMode={editMode}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {(mergedContent.testimonials.items || []).map((testimonial, index) => (
                <EditableCard 
                  key={index}
                  index={index}
                  editMode={editMode}
                  onDelete={() => handleDeleteItem('testimonials', 'items', index)}
                  className="bg-[#0d1218] border border-white/5 rounded-2xl p-6 hover:border-[#3a7189]/30 transition-all"
                >
                  {/* Stars */}
                  <div className="mb-4">
                    <EditableRating
                      value={testimonial.rating || 5}
                      onChange={(rating) => onUpdateContent(`testimonials.items.${index}.rating`, rating)}
                      editMode={editMode}
                    />
                  </div>
                  
                  {/* Quote */}
                  <EditableText
                    tag="p"
                    value={testimonial.text || 'Excelente plataforma.'}
                    onChange={onUpdateContent}
                    path={`testimonials.items.${index}.text`}
                    className="text-[#93a5af] mb-4 text-sm leading-relaxed"
                    multiline
                    editMode={editMode}
                  />
                  
                  {/* Author */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3a7189] to-[#1c262d] flex items-center justify-center text-white font-bold text-sm">
                      {(testimonial.name?.charAt(0) || 'U').toUpperCase()}
                    </div>
                    <div>
                      <EditableText
                        tag="p"
                        value={testimonial.name || 'Cliente'}
                        onChange={onUpdateContent}
                        path={`testimonials.items.${index}.name`}
                        className="text-white font-semibold text-sm"
                        editMode={editMode}
                      />
                      <EditableText
                        tag="p"
                        value={testimonial.role || 'Investidor'}
                        onChange={onUpdateContent}
                        path={`testimonials.items.${index}.role`}
                        className="text-[#93a5af]/70 text-xs"
                        editMode={editMode}
                      />
                    </div>
                  </div>
                </EditableCard>
              ))}
            </div>

            {/* Add Testimonial Button */}
            {editMode === 'visual' && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => handleAddItem('testimonials', 'items', {
                    name: 'Novo cliente',
                    role: 'Investidor',
                    rating: 5,
                    text: 'Escreva o testemunho aqui...'
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-[#3a7189]/20 text-[#3a7189] rounded-lg hover:bg-[#3a7189]/30 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar testemunho
                </button>
              </div>
            )}
          </div>
        </EditableSection>
      )}

      {/* Videos Section */}
      {sectionVisibility.videos && (
        <EditableSection 
          id="videos" 
          editMode={editMode}
          bgColor="#0d1218"
          onDelete={handleDeleteSection}
          onColorChange={handleSectionColorChange}
          className="py-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <EditableText
                tag="h2"
                value={mergedContent.videos.title }
                onChange={onUpdateContent}
                path="videos.title"
                className="text-3xl font-bold text-white mb-3 tracking-tight"
                editMode={editMode}
              />
              <EditableText
                tag="p"
                value={mergedContent.videos.subtitle }
                onChange={onUpdateContent}
                path="videos.subtitle"
                className="text-lg text-[#93a5af] max-w-2xl mx-auto"
                editMode={editMode}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {(mergedContent.videos.items || []).map((video, index) => (
                <EditableCard 
                  key={video.id || index}
                  index={index}
                  editMode={editMode}
                  onDelete={() => handleDeleteItem('videos', 'items', index)}
                  className="bg-[#1c262d] border border-white/5 rounded-2xl overflow-hidden hover:border-[#3a7189]/30 transition-all"
                >
                  <div className="aspect-video relative bg-[#0d1218] flex items-center justify-center">
                    {video.embedUrl ? (
                      <iframe
                        src={video.embedUrl}
                        title={video.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#3a7189]/30 flex items-center justify-center cursor-pointer hover:bg-[#3a7189]/50 transition-colors">
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <EditableText
                      tag="h3"
                      value={video.title }
                      onChange={onUpdateContent}
                      path={`videos.items.${index}.title`}
                      className="text-base font-bold text-white mb-1"
                      editMode={editMode}
                    />
                    <EditableText
                      tag="p"
                      value={video.description }
                      onChange={onUpdateContent}
                      path={`videos.items.${index}.description`}
                      className="text-[#93a5af] text-sm"
                      editMode={editMode}
                    />
                    {editMode === 'visual' && (
                      <div className="mt-3">
                        <input
                          type="text"
                          placeholder="URL de YouTube/Vimeo embed"
                          value={video.embedUrl || ''}
                          onChange={(e) => onUpdateContent(`videos.items.${index}.embedUrl`, e.target.value)}
                          className="w-full bg-[#0d1218] border border-white/10 text-white text-xs rounded px-2 py-1.5 focus:border-[#3a7189] focus:outline-none"
                        />
                      </div>
                    )}
                  </div>
                </EditableCard>
              ))}
            </div>
          </div>
        </EditableSection>
      )}

      {/* FAQ Section */}
      {sectionVisibility.faq && (
        <EditableSection 
          id="faq" 
          editMode={editMode}
          bgColor="#1c262d"
          onDelete={handleDeleteSection}
          onColorChange={handleSectionColorChange}
          className="py-20"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <EditableText
                tag="h2"
                value={mergedContent.faq.title}
                onChange={onUpdateContent}
                path="faq.title"
                className="text-3xl font-bold text-white mb-3 tracking-tight"
                editMode={editMode}
              />
              <EditableText
                tag="p"
                value={mergedContent.faq.subtitle}
                onChange={onUpdateContent}
                path="faq.subtitle"
                className="text-lg text-[#93a5af] max-w-2xl mx-auto"
                editMode={editMode}
              />
            </div>

            <div className="space-y-4">
              {(mergedContent.faq.items || []).map((faq, index) => (
                <EditableCard 
                  key={faq.id || index}
                  index={index}
                  editMode={editMode}
                  onDelete={() => handleDeleteItem('faq', 'items', index)}
                  className="bg-[#0d1218] border border-white/5 rounded-2xl overflow-hidden hover:border-[#3a7189]/30 transition-all"
                >
                  <div className="p-6">
                    <EditableText
                      tag="h3"
                      value={faq.question}
                      onChange={onUpdateContent}
                      path={`faq.items.${index}.question`}
                      className="text-lg font-semibold text-white mb-3"
                      editMode={editMode}
                    />
                    <EditableText
                      tag="p"
                      value={faq.answer}
                      onChange={onUpdateContent}
                      path={`faq.items.${index}.answer`}
                      className="text-[#93a5af] text-sm leading-relaxed"
                      multiline
                      editMode={editMode}
                    />
                  </div>
                </EditableCard>
              ))}
            </div>

            {/* Add FAQ Button */}
            {editMode === 'visual' && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => handleAddItem('faq', 'items', {
                    question: 'Nova pergunta?',
                    answer: 'Resposta à pergunta...'
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-[#3a7189]/20 text-[#3a7189] rounded-lg hover:bg-[#3a7189]/30 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar pergunta
                </button>
              </div>
            )}
          </div>
        </EditableSection>
      )}

      {/* CTA Section */}
      {sectionVisibility.cta && (
        <EditableSection 
          id="cta" 
          editMode={editMode}
          bgColor="transparent"
          onDelete={handleDeleteSection}
          className="py-20 bg-gradient-to-r from-[#3a7189] to-[#247029]"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <EditableText
              tag="h2"
              value={mergedContent.cta.title}
              onChange={onUpdateContent}
              path="cta.title"
              className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight"
              editMode={editMode}
            />
            <EditableText
              tag="p"
              value={mergedContent.cta.subtitle }
              onChange={onUpdateContent}
              path="cta.subtitle"
              className="text-lg text-green-100 mb-8 max-w-2xl mx-auto"
              multiline
              editMode={editMode}
            />
            <button className="bg-white text-[#3a7189] rounded-full px-8 py-3 text-sm font-bold shadow-xl flex items-center gap-2 mx-auto hover:bg-green-50 transition-colors">
              <EditableText
                value={mergedContent.cta.buttonText }
                onChange={onUpdateContent}
                path="cta.buttonText"
                editMode={editMode}
              />
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </EditableSection>
      )}

      {/* Contact Section */}
      {sectionVisibility.contact && (
        <EditableSection 
          id="contact" 
          editMode={editMode}
          bgColor="#0d1218"
          onDelete={handleDeleteSection}
          onColorChange={handleSectionColorChange}
          className="py-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <EditableText
                  tag="h2"
                  value={mergedContent.contact.title}
                  onChange={onUpdateContent}
                  path="contact.title"
                  className="text-3xl font-bold text-white mb-3 tracking-tight"
                  editMode={editMode}
                />
                <EditableText
                  tag="p"
                  value={mergedContent.contact.subtitle }
                  onChange={onUpdateContent}
                  path="contact.subtitle"
                  className="text-lg text-[#93a5af] mb-8"
                  multiline
                  editMode={editMode}
                />
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1c262d] border border-white/5">
                    <div className="w-10 h-10 rounded-xl bg-[#3a7189]/20 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-[#3a7189]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#93a5af]/70">Email</p>
                      <EditableText
                        tag="p"
                        value={mergedContent.contact.email }
                        onChange={onUpdateContent}
                        path="contact.email"
                        className="text-sm font-semibold text-white"
                        editMode={editMode}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1c262d] border border-white/5">
                    <div className="w-10 h-10 rounded-xl bg-[#3a7189]/20 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#3a7189]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#93a5af]/70">Localização</p>
                      <EditableText
                        tag="p"
                        value={mergedContent.contact.location || 'Lisboa, Portugal'}
                        onChange={onUpdateContent}
                        path="contact.location"
                        className="text-sm font-semibold text-white"
                        editMode={editMode}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#1c262d] border border-white/5 rounded-2xl p-6">
                <EditableText
                  tag="h3"
                  value={mergedContent.contact.legalTitle || 'Informação Legal'}
                  onChange={onUpdateContent}
                  path="contact.legalTitle"
                  className="text-xl font-bold text-white mb-4"
                  editMode={editMode}
                />
                <div className="space-y-3 text-[#93a5af] text-sm">
                  <EditableText
                    tag="p"
                    value={mergedContent.contact.legalText || 'SGFI Capital - Smart Global Financial Insights.'}
                    onChange={onUpdateContent}
                    path="contact.legalText"
                    multiline
                    editMode={editMode}
                  />
                  <p>© {new Date().getFullYear()} SGFI Capital. Todos los derechos reservados.</p>
                </div>
              </div>
            </div>
          </div>
        </EditableSection>
      )}

      {/* Footer */}
      <footer className="py-6 bg-[#0d1218] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-6 w-auto" />
            <p className="text-[#93a5af]/50 text-xs">
              © {new Date().getFullYear()} SGFI Capital
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
